// Define user roles - Updated to match Users page roles
export type UserRole = 'admin' | 'owner' | 'manager' | 'cashier' | 'salesrep' | 'storekeeper' | 'accountant' | 'driver' | '';

// Role options configuration for consistency across the application
export const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'cashier', label: 'Cashier' },
  { value: 'salesrep', label: 'Sales Representative' },
  { value: 'storekeeper', label: 'Storekeeper' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'driver', label: 'Driver' }
];

const KNOWN_ROLES: Exclude<UserRole, ''>[] = [
  'admin',
  'owner',
  'manager',
  'cashier',
  'salesrep',
  'storekeeper',
  'accountant',
  'driver',
];

// Get the current user role from local storage or session
export const getUserRole = (): UserRole => {
  const storedRole = localStorage.getItem('user_role')?.toLowerCase();
  if (!storedRole || !KNOWN_ROLES.includes(storedRole as Exclude<UserRole, ''>)) {
    return '';
  }
  return storedRole as Exclude<UserRole, ''>;
};

// Define the card interface
export interface DashboardCard {
  title: string;
  description: string;
  bgColor: string;
  path: string;
  buttonText: string;
}

// Get dashboard cards based on user role
export const getDashboardCards = (role: UserRole): DashboardCard[] => {
  const allCards: Record<string, DashboardCard> = {
    customers: {
      title: "Customers",
      description: "Manage your customer database",
      bgColor: "bg-[#17a2b8]",
      path: "/customers",
      buttonText: "Manage Customers"
    },
    products: {
      title: "Products",
      description: "Manage your product catalog",
      bgColor: "bg-[#28a745]",
      path: "/products",
      buttonText: "Manage Products"
    },
    sales: {
      title: "Invoices",
      description: "Track customer invoices and payments",
      bgColor: "bg-[#6c757d]",
      path: "/sales",
      buttonText: "View Invoices"
    },
    categories: {
      title: "Categories",
      description: "Organize products by categories",
      bgColor: "bg-[#17a2b8]",
      path: "/categories",
      buttonText: "Manage Categories"
    },
    brands: {
      title: "Brands",
      description: "Manage product brands",
      bgColor: "bg-[#28a745]",
      path: "/brands",
      buttonText: "Manage Brands"
    },
    units: {
      title: "Units",
      description: "Manage measurement units",
      bgColor: "bg-[#dc3545]",
      path: "/units",
      buttonText: "Manage Units"
    },
    inventory: {
      title: "Inventory",
      description: "Manage stock levels and inventory tracking",
      bgColor: "bg-[#6c757d]",
      path: "/inventory",
      buttonText: "Manage Inventory"
    },
    suppliers: {
      title: "Suppliers",
      description: "Manage your suppliers",
      bgColor: "bg-[#28a745]",
      path: "/suppliers",
      buttonText: "Manage Suppliers"
    },
    purchases: {
      title: "Bills",
      description: "Track supplier bills and payments",
      bgColor: "bg-[#dc3545]",
      path: "/purchases",
      buttonText: "View Bills"
    },
    settings: {
      title: "Settings",
      description: "Company profile and system users",
      bgColor: "bg-[#6c757d]",
      path: "/settings",
      buttonText: "Open Settings"
    },
    pos: {
      title: "POS",
      description: "Point of Sale system",
      bgColor: "bg-[#28a745]",
      path: "/pos",
      buttonText: "Open POS"
    },
    reports: {
      title: "Reports",
      description: "View and export business reports",
      bgColor: "bg-[#6c757d]",
      path: "/reports",
      buttonText: "View Reports"
    },
    accounts: {
      title: "Accounts",
      description: "Journal, statements, and chart of accounts",
      bgColor: "bg-[#6c757d]",
      path: "/accounts",
      buttonText: "View Accounts"
    }
  };

  // Return role-specific cards based on role hierarchy and responsibilities
  switch (role) {
    case 'admin':
      // Full system access
      return Object.values(allCards);

    case 'owner':
      // Business owner - full access including user management
      return [
        allCards.customers,
        allCards.products,
        allCards.sales,
        allCards.categories,
        allCards.brands,
        allCards.suppliers,
        allCards.purchases,
        allCards.inventory,
        allCards.pos,
        allCards.reports,
        allCards.accounts,
        allCards.settings
      ];
    
    case 'manager':
      // Business operations management
      return [
        allCards.customers,
        allCards.products,
        allCards.sales,
        allCards.categories,
        allCards.brands,
        allCards.suppliers,
        allCards.purchases,
        allCards.inventory,
        allCards.pos,
        allCards.reports,
        allCards.accounts
      ];

    case 'cashier':
      return [
        allCards.customers,
        allCards.products,
        allCards.sales,
        allCards.pos
      ];
    
    case 'salesrep':
      // Sales representative - customer and sales focused
      return [
        allCards.customers,
        allCards.products,
        allCards.sales,
        allCards.pos
      ];
    
    case 'storekeeper':
      // Inventory and stock management
      return [
        allCards.products,
        allCards.categories,
        allCards.brands,
        allCards.units,
        allCards.inventory
      ];
    
    case 'accountant':
      // Financial operations and reporting
      return [
        allCards.sales,
        allCards.purchases,
        allCards.suppliers,
        allCards.customers,
        allCards.pos,
        allCards.reports,
        allCards.accounts
      ];
    
    case 'driver':
      // Delivery and logistics
      return [
        allCards.sales,
        allCards.customers
      ];
    
    default:
      // Unknown or missing role — deny access
      return [];
  }
};

// Utility function to check if a role has access to a specific module
export const hasModuleAccess = (role: UserRole, module: string): boolean => {
  const cards = getDashboardCards(role);
  return cards.some(card => card.path === `/${module}`);
};

// Utility function to get role display name
export const getRoleDisplayName = (role: UserRole): string => {
  if (!role) return 'No role';
  const roleOption = ROLE_OPTIONS.find(option => option.value === role);
  return roleOption ? roleOption.label : role;
};

// Utility function to check if role is admin or owner
export const isAdminOrOwner = (role: UserRole): boolean => {
  return role === 'admin' || role === 'owner';
};

// Utility function to check if role can manage users
export const canManageUsers = (role: UserRole): boolean => {
  return role === 'admin';
};
